import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { database } from '../firebaseConfig';
import { ref, onValue, query as rtdbQuery, limitToFirst, startAt, get, set, update, endAt, limitToLast, equalTo, orderByChild } from 'firebase/database';
import { getFirestore, collection, query, where, orderBy, limit, getDocs, startAfter, doc, updateDoc, getCountFromServer, Timestamp, getDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import SmartInboxEmail from './SmartInboxEmail';
import EmailDraftsPreview from './EmailDraftsPreview';
import EmailAiPopup from './EmailAiPopup';
import DraftBulkMode from './EmailAiModes/DraftBulkMode';
import ScheduledEmails from './ScheduledEmails';
import InboxFilters from './InboxFilters';
import './SmartInbox.css';

const SmartInbox = ({ userId }) => {
  const [emails, setEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [repliedEmails, setRepliedEmails] = useState([]);
  const [forwardedEmails, setForwardedEmails] = useState([]);
  const [deletedEmails, setDeletedEmails] = useState([]);
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [safeGuardDrafts, setSafeGuardDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [inboxActive, setInboxActive] = useState(false);
  
  // Pagination states für alle E-Mail-Typen
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreEmails, setHasMoreEmails] = useState(true);
  const [lastEmailDoc, setLastEmailDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // OPTIMIERT: Echte Pagination für Sent/Deleted/Scheduled
  const [lastSentDoc, setLastSentDoc] = useState(null);
  const [lastDeletedDoc, setLastDeletedDoc] = useState(null);
  const [lastScheduledDoc, setLastScheduledDoc] = useState(null);
  const [hasMoreSentEmails, setHasMoreSentEmails] = useState(false);
  const [hasMoreDeletedEmails, setHasMoreDeletedEmails] = useState(false);
  const [hasMoreScheduledEmails, setHasMoreScheduledEmails] = useState(false);
  
  // NEU: Separate loading state for sent emails
  const [loadingSentMore, setLoadingSentMore] = useState(false);
  
  const PAGE_SIZE = 20; // Erhöht für bessere UX
  const SENT_PAGE_SIZE = 10; // Neue Konstante für Sent-E-Mails
  
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    spam: 0,
    sent: 0,
    scheduled: 0,
    replied: 0,
    deleted: 0,
    safeguard: 0
  });
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [previousAccount, setPreviousAccount] = useState('all'); // Track previous account to detect real changes
  const [enthusiasmFilter, setEnthusiasmFilter] = useState('all'); // 'all' or 'enthusiastic' (>=5)
  const [enthusiasmStats, setEnthusiasmStats] = useState({
    low: 0,    // 1-3
    medium: 0, // 4-6
    high: 0,   // 7-10
    unknown: 0 // not yet analyzed
  });
  const [showEnthusiasmDistribution, setShowEnthusiasmDistribution] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [autoProcessEmails, setAutoProcessEmails] = useState(false);
  const [selectedTag, setSelectedTag] = useState('all');
  const [aiTags, setAiTags] = useState([]);
  const [tagCounts, setTagCounts] = useState({});

  // Draft EmailAiPopup States
  const [showDraftEmailPopup, setShowDraftEmailPopup] = useState(false);
  const [draftEmailData, setDraftEmailData] = useState(null);

  // Draft Bulk Mode States
  const [showDraftBulkMode, setShowDraftBulkMode] = useState(false);
  const [bulkDrafts, setBulkDrafts] = useState([]);
  const [selectedDraftIndex, setSelectedDraftIndex] = useState(0);
  const [userSettings, setUserSettings] = useState({});
  const [aiSettings, setAiSettings] = useState({});
  const [userTopics, setUserTopics] = useState({});
  const [isTopicsLoaded, setIsTopicsLoaded] = useState(false);
  const [topicTypeMapping, setTopicTypeMapping] = useState({});

  // NEU: States für die Migration von RTDB zu Firestore
  const [needsMigration, setNeedsMigration] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationMessage, setMigrationMessage] = useState('');

  const functions = getFunctions();
  const manualFetchEmails = httpsCallable(functions, 'manualFetchSmartDomainEmails');
  const analyzeEmailEndpoint = httpsCallable(functions, 'analyzeEmailEndpoint');
  const processEmailWithAgents = httpsCallable(functions, 'processEmailWithAgents');
  const migrateInboxToFirestore = httpsCallable(functions, 'migrateInboxToFirestore');

  // NEU: Initialisierung von Firestore
  const firestoreDB = getFirestore();

  // =================================================================================
  // == DATA LOADING & HANDLER FUNCTIONS
  // =================================================================================

  const formatEmailAddress = useCallback((email) => {
    if (!email) return '';
    if (email.length <= 15) return email;
    
    const parts = email.split('@');
    if (parts.length !== 2) return email;
    
    const username = parts[0];
    const domain = parts[1];
    
    const shortUsername = username.length > 8 
      ? username.substring(0, 6) + '...' 
      : username;
    
    return `${shortUsername}@${domain}`;
  }, []);

  // Hilfsfunktion zum Extrahieren des Sender-Namens
  const extractSenderName = useCallback((fromEmail) => {
    if (!fromEmail) return 'Unknown';
    
    // Extrahiere Name aus "Name <email@domain.com>" Format
    const nameMatch = fromEmail.match(/^([^<]+)</);
    if (nameMatch) {
      return nameMatch[1].trim();
    }
    
    // Falls nur E-Mail-Adresse, extrahiere den Teil vor @
    const emailMatch = fromEmail.match(/([^@]+)@/);
    return emailMatch ? emailMatch[1] : fromEmail;
  }, []);

  // Hilfsfunktion zum Extrahieren der E-Mail-Adresse
  const extractEmailFromString = useCallback((fromString) => {
    if (!fromString) return '';
    
    // Extrahiere E-Mail aus "Name <email@domain.com>" Format
    const emailMatch = fromString.match(/<([^>]+)>/);
    if (emailMatch) {
      return emailMatch[1].trim();
    }
    
    // Falls es nur eine E-Mail-Adresse ist
    if (fromString.includes('@')) {
      return fromString.trim();
    }
    
    return '';
  }, []);

  // NEU & OPTIMIERT: Lädt alle E-Mail-Zählungen effizient und global aus Firestore
  const loadEmailCounts = useCallback(async () => {
      if (!userId || isMigrating) return;
      console.log(`🚀 Lade globale E-Mail-Zählungen aus Firestore...`);
  
      try {
          const inboxCollectionRef = collection(firestoreDB, 'world_inbox', userId, 'emails');
          const sentCollectionRef = collection(firestoreDB, 'world_sent', userId, 'emails');
          const scheduledCollectionRef = collection(firestoreDB, 'world_scheduled_emails', userId, 'emails');
  
          // Queries für Inbox (global)
          const totalQuery = query(inboxCollectionRef, where('deleted', '==', false));
          const unreadQuery = query(inboxCollectionRef, where('deleted', '==', false), where('isRead', '==', false));
          const spamQuery = query(inboxCollectionRef, where('isSpam', '==', true), where('deleted', '==', false));
          const deletedQuery = query(inboxCollectionRef, where('deleted', '==', true));
          
          // Queries für Sent & Replied (global)
          const sentQuery = query(sentCollectionRef);
          const repliedQuery = query(sentCollectionRef, where('emailType', 'in', ['custom_reply', 'draft_reply']));
  
          // Query für Scheduled (global)
          const scheduledQuery = query(scheduledCollectionRef);

          // Query für SafeGuard Drafts
          const safeGuardCollectionRef = collection(firestoreDB, 'world_drafts', userId, 'emails');
          const safeGuardQuery = query(
            safeGuardCollectionRef,
            where('status', '==', 'draft'),
            where('reason', '==', 'safeguard_rejection')
          );
  
          const [
              totalSnapshot,
              unreadSnapshot,
              spamSnapshot,
              deletedSnapshot,
              sentSnapshot,
              repliedSnapshot,
              scheduledSnapshot,
              safeGuardSnapshot,
          ] = await Promise.all([
              getCountFromServer(totalQuery),
              getCountFromServer(unreadQuery),
              getCountFromServer(spamQuery),
              getCountFromServer(deletedQuery),
              getCountFromServer(sentQuery),
              getCountFromServer(repliedQuery),
              getCountFromServer(scheduledQuery),
              getCountFromServer(safeGuardQuery),
          ]);
  
          const newStats = {
              total: totalSnapshot.data().count,
              unread: unreadSnapshot.data().count,
              spam: spamSnapshot.data().count,
              deleted: deletedSnapshot.data().count,
              sent: sentSnapshot.data().count,
              replied: repliedSnapshot.data().count,
              scheduled: scheduledSnapshot.data().count,
              safeguard: safeGuardSnapshot.data().count,
          };
  
          setStats(newStats);
          console.log(`🚀 Globale Firestore-Zählungen geladen:`, newStats);
  
      } catch (error) {
          console.error("Fehler beim Laden der E-Mail-Zählungen aus Firestore: ", error);
          // setError("Fehler beim Laden der Statistiken."); // Fehler nicht anzeigen, um UI nicht zu blockieren
      }
  }, [userId, firestoreDB, isMigrating]);

  // NEU: Lädt die Zählungen für jeden AI-Tag aus Firestore
  const loadTagCounts = useCallback(async (targetAccount = 'all') => {
    if (!userId || isMigrating || aiTags.length === 0) {
      setTagCounts({});
      return;
    }
    console.log(`📊 Lade Tag-Zählungen für Account: ${targetAccount}...`);

    try {
        const inboxCollectionRef = collection(firestoreDB, 'world_inbox', userId, 'emails');
        
        const counts = {};
        const promises = aiTags.map(async (tag) => {
            if (!tag.tag) return; // Überspringen, falls das Tag-Objekt ungültig ist

            let q = query(
                inboxCollectionRef,
                where('deleted', '==', false),
                where(`tags.${tag.tag}`, '==', true)
            );

            // Filtere nach Konto, wenn nicht 'all' ausgewählt ist
            if (targetAccount !== 'all' && !['sent', 'replied', 'forwarded', 'deleted', 'scheduled'].includes(targetAccount)) {
                q = query(q, where('accountId', '==', targetAccount));
            }
            
            const snapshot = await getCountFromServer(q);
            counts[tag.tag] = snapshot.data().count;
        });

        await Promise.all(promises);
        
        setTagCounts(counts);
        console.log(`📊 Tag-Zählungen geladen:`, counts);
    } catch (error) {
        console.error("Fehler beim Laden der Tag-Zählungen:", error);
    }
  }, [userId, firestoreDB, isMigrating, aiTags]);

  // 🔥 Server-Side Filtered E-Mail Loader: Includes enthusiasm and tag filters
  const loadEmailsPage = useCallback(async (pageNumber = 1, lastVisibleDocs = null, targetAccount = 'all') => {
    if (!userId || isMigrating) return;

    const isInitialLoad = pageNumber === 1;

    if (isInitialLoad) {
      setLoading(true);
      setEmails([]);
      setLastEmailDoc(null);
      setHasMoreEmails(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const emailsCollectionRef = collection(firestoreDB, 'world_inbox', userId, 'emails');
      
      // Helper function to build queries with filters
      const buildFilteredQuery = (baseQuery) => {
        let filteredQuery = baseQuery;
        
        // Add enthusiasm filter (only for inbox emails, not sent/replied/etc.)
        if (enthusiasmFilter === 'enthusiastic') {
          filteredQuery = query(filteredQuery, where('enthusiasmScore', '>=', 5));
        }
        
        // Add AI tag filter (only for inbox emails)
        if (selectedTag !== 'all') {
          filteredQuery = query(filteredQuery, where(`tags.${selectedTag}`, '==', true));
        }
        
        return filteredQuery;
      };
      
      if (targetAccount === 'all') {
        // Multi-Account-Modus: Lade 10 E-Mails von jedem Account mit Filtern
        if (!emailAccounts || emailAccounts.length === 0) {
          console.log("Keine E-Mail-Accounts konfiguriert");
          setEmails([]);
          setHasMoreEmails(false);
          return;
        }

        const allEmails = [];
        const newLastVisibleDocs = {};
        let hasMoreFromAnyAccount = false;

        // Parallel alle Accounts abfragen mit Filtern
        const accountPromises = emailAccounts.map(async (account) => {
          const accountId = account.id;
          
          let baseQuery = query(
            emailsCollectionRef,
            where('accountId', '==', accountId),
            where('deleted', '==', false),
            orderBy('timestamp', 'desc'),
            limit(10) // 10 pro Account
          );

          // Apply server-side filters
          let q = buildFilteredQuery(baseQuery);

          // Pagination pro Account
          if (lastVisibleDocs && lastVisibleDocs[accountId]) {
            q = query(q, startAfter(lastVisibleDocs[accountId]));
          }

          const snapshot = await getDocs(q);
          const accountEmails = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Speichere letztes Dokument pro Account für Pagination
          if (snapshot.docs.length > 0) {
            newLastVisibleDocs[accountId] = snapshot.docs[snapshot.docs.length - 1];
          }

          // Prüfe ob dieser Account noch mehr E-Mails hat
          if (snapshot.docs.length === 10) {
            hasMoreFromAnyAccount = true;
          }

          return accountEmails;
        });

        const results = await Promise.all(accountPromises);
        
        // Alle E-Mails zusammenführen
        results.forEach(accountEmails => {
          allEmails.push(...accountEmails);
        });

        // Nach timestamp sortieren (neueste zuerst)
        allEmails.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

        console.log(`🔥 Server-side filtered: ${allEmails.length} E-Mails von ${emailAccounts.length} Accounts (enthusiasm: ${enthusiasmFilter}, tag: ${selectedTag})`);

        setHasMoreEmails(hasMoreFromAnyAccount);

        if (isInitialLoad) {
          setEmails(allEmails);
        } else {
          setEmails(prevEmails => [...prevEmails, ...allEmails]);
        }
        
        setLastEmailDoc(newLastVisibleDocs);

      } else {
        // Single-Account-Modus mit Filtern
        let baseQuery = query(
          emailsCollectionRef,
          where('accountId', '==', targetAccount),
          where('deleted', '==', false),
          orderBy('timestamp', 'desc'),
          limit(20)
        );

        // Apply server-side filters
        let q = buildFilteredQuery(baseQuery);

        if (lastVisibleDocs && typeof lastVisibleDocs === 'object' && lastVisibleDocs[targetAccount]) {
          q = query(q, startAfter(lastVisibleDocs[targetAccount]));
        }

        const documentSnapshots = await getDocs(q);
        const newEmails = documentSnapshots.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        const newLastVisible = documentSnapshots.docs[documentSnapshots.docs.length - 1];
        setHasMoreEmails(documentSnapshots.docs.length === 20);

        console.log(`🔥 Server-side filtered: ${newEmails.length} E-Mails für Account ${targetAccount} (enthusiasm: ${enthusiasmFilter}, tag: ${selectedTag})`);

        if (isInitialLoad) {
          setEmails(newEmails);
        } else {
          setEmails(prevEmails => [...prevEmails, ...newEmails]);
        }
        
        setLastEmailDoc({ [targetAccount]: newLastVisible });
      }

      setCurrentPage(pageNumber);

    } catch (error) {
      console.error("🔥 Fehler beim Server-Side Filtering der E-Mails: ", error);
      setError("Fehler beim Laden der E-Mails.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [userId, firestoreDB, isMigrating, emailAccounts, enthusiasmFilter, selectedTag]);

  // Load sent emails (simple pattern like scheduled)
  useEffect(() => {
    if (!userId || isMigrating || !['sent', 'replied'].includes(selectedAccount)) {
      if (['sent', 'replied'].includes(selectedAccount)) {
        setSentEmails([]);
        setLastSentDoc(null);
      }
      return;
    }

    console.log('Loading sent emails...');
    setLoading(true);

    const sentCollectionRef = collection(firestoreDB, 'world_sent', userId, 'emails');
    const q = query(sentCollectionRef, orderBy('timestamp', 'desc'), limit(SENT_PAGE_SIZE));

    getDocs(q)
      .then(snapshot => {
        const sentEmailsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          isSent: true,
        }));
        setSentEmails(sentEmailsData);
        setHasMoreSentEmails(snapshot.docs.length === SENT_PAGE_SIZE);
        setLastSentDoc(snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null);
        console.log(`${sentEmailsData.length} sent emails loaded (initial batch)`);
      })
      .catch(error => {
        console.error("Error loading sent emails:", error);
        setError("Error loading sent emails.");
      })
      .finally(() => setLoading(false));
  }, [userId, firestoreDB, isMigrating, selectedAccount]);

  // Load deleted emails (simple pattern like scheduled)
  useEffect(() => {
    if (!userId || isMigrating || selectedAccount !== 'deleted') {
      if (selectedAccount === 'deleted') setDeletedEmails([]);
      return;
    }

    console.log('Loading deleted emails...');
    setLoading(true);

    const emailsCollectionRef = collection(firestoreDB, 'world_inbox', userId, 'emails');
    const q = query(
      emailsCollectionRef,
      where('deleted', '==', true),
      orderBy('timestamp', 'desc'),
      limit(PAGE_SIZE)
    );

    getDocs(q)
      .then(snapshot => {
        const deletedEmailsArray = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          isDeleted: true,
        }));
        setDeletedEmails(deletedEmailsArray);
        setHasMoreDeletedEmails(snapshot.docs.length === PAGE_SIZE);
        console.log(`${deletedEmailsArray.length} deleted emails loaded`);
      })
      .catch(error => {
        console.error('Error loading deleted emails:', error);
        setError('Error loading deleted emails.');
      })
      .finally(() => setLoading(false));
  }, [userId, isMigrating, firestoreDB, selectedAccount]);

  // Load More Handler (nur für Inbox - sent/deleted zeigen alle)
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMoreEmails) return;
    loadEmailsPage(currentPage + 1, lastEmailDoc, selectedAccount);
  }, [loadingMore, hasMoreEmails, currentPage, lastEmailDoc, selectedAccount, loadEmailsPage]);

  // Load More Handler für Sent Emails
  const handleLoadMoreSent = useCallback(() => {
    if (loadingSentMore || !hasMoreSentEmails || !lastSentDoc) return;
    
    setLoadingSentMore(true);
    
    const sentCollectionRef = collection(firestoreDB, 'world_sent', userId, 'emails');
    const q = query(
      sentCollectionRef, 
      orderBy('timestamp', 'desc'), 
      startAfter(lastSentDoc),
      limit(SENT_PAGE_SIZE)
    );

    getDocs(q)
      .then(snapshot => {
        const newSentEmails = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          isSent: true,
        }));
        
        // Füge die neuen E-Mails zu den bestehenden hinzu
        setSentEmails(prevEmails => [...prevEmails, ...newSentEmails]);
        setHasMoreSentEmails(snapshot.docs.length === SENT_PAGE_SIZE);
        setLastSentDoc(snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null);
        
        console.log(`${newSentEmails.length} weitere sent emails geladen`);
      })
      .catch(error => {
        console.error("Error loading more sent emails:", error);
        setError("Error loading more sent emails.");
      })
      .finally(() => setLoadingSentMore(false));
  }, [loadingSentMore, hasMoreSentEmails, lastSentDoc, userId, firestoreDB]);

  // Funktion zum manuellen Abrufen neuer E-Mails
  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
        console.log("Manuelle Aktualisierung wird getriggert...");
        await manualFetchEmails({ userId }); // Bestehende Funktion zum Abrufen vom IMAP-Server
        
        // Nach dem Abruf, lade die erste Seite neu aus Firestore
        await loadEmailsPage(1, null, selectedAccount);

    } catch (err) {
        console.error("Fehler bei der manuellen Aktualisierung:", err);
        setError('Fehler beim Abrufen neuer E-Mails.');
    } finally {
        setRefreshing(false);
    }
  };

  // KORREKTUR: Diese Funktionen verwenden wieder die originalen RTDB-Methoden, um `no-undef` zu beheben.
  const handleDeleteEmail = (deletedEmail) => {
    if (!userId || !deletedEmail?.id) return;
    
    // 🛡️ [SAFEGUARD] SafeGuard Drafts sind bereits in SmartInboxEmail.js gelöscht worden
    if (deletedEmail.isSafeGuardDraft || deletedEmail.reason === 'safeguard_rejection' || deletedEmail.emailType === 'safeguard') {
      console.log(`🛡️ [SAFEGUARD] SafeGuard draft ${deletedEmail.id} already deleted from Firestore, removing from UI`, {
        isSafeGuardDraft: deletedEmail.isSafeGuardDraft,
        reason: deletedEmail.reason,
        emailType: deletedEmail.emailType,
        currentDraftsCount: safeGuardDrafts.length
      });
      
      // Entferne aus dem lokalen State (bereits aus Firestore gelöscht)
      setSafeGuardDrafts(prevDrafts => {
        const newDrafts = prevDrafts.filter(draft => draft.id !== deletedEmail.id);
        console.log(`🛡️ [SAFEGUARD] Updated SafeGuard drafts list: ${prevDrafts.length} → ${newDrafts.length}`);
        return newDrafts;
      });
      
      // Entferne auch aus der Haupt-E-Mail-Liste falls dort vorhanden
      setEmails(prevEmails => prevEmails.filter(email => email.id !== deletedEmail.id));
      
      loadEmailCounts(); // Aktualisiere Statistiken
      console.log(`🛡️ [SAFEGUARD] SafeGuard draft ${deletedEmail.id} successfully removed from UI`);
      return;
    }
    
    console.log(`Marking email ${deletedEmail.id} as deleted in Firestore.`);
    const emailRef = doc(firestoreDB, 'world_inbox', userId, 'emails', deletedEmail.id);
    
    updateDoc(emailRef, {
      deleted: true,
      deletedAt: Timestamp.now() // Setze den Löschzeitpunkt
    }).then(() => {
      console.log(`Email ${deletedEmail.id} erfolgreich in Firestore als gelöscht markiert.`);
      // Entferne die E-Mail aus dem lokalen Zustand, damit sie sofort aus der UI verschwindet
      setEmails(prevEmails => prevEmails.filter(email => email.id !== deletedEmail.id));
      // Statistiken neu laden, um die Zählung zu aktualisieren
      loadEmailCounts();
    }).catch(error => {
      console.error("Fehler beim Löschen der E-Mail in Firestore: ", error);
      setError("Fehler beim Löschen der E-Mail.");
    });
  };

  const handleMarkAsReplied = async (emailToMark) => {
    if (!userId || !emailToMark?.id) return;

    console.log(`Marking email ${emailToMark.id} as replied in Firestore.`);
    const emailRef = doc(firestoreDB, 'world_inbox', userId, 'emails', emailToMark.id);

    try {
      await updateDoc(emailRef, {
        isReplied: true, // ✅ KORRIGIERT: Verwende isReplied statt replied
        replied: true,   // ✅ BEHALTE: Für Rückwärtskompatibilität mit alten E-Mails
        isRead: true,    // Eine beantwortete Mail sollte auch als gelesen markiert werden
        repliedAt: serverTimestamp() // ✅ NEU: Zeitstempel für Reply
      });

      // Aktualisiere den lokalen Zustand, um die UI sofort zu aktualisieren
      setEmails(prevEmails =>
        prevEmails.map(email =>
          email.id === emailToMark.id ? { 
            ...email, 
            isReplied: true, // ✅ KORRIGIERT: Verwende isReplied statt replied
            replied: true,   // ✅ BEHALTE: Für Rückwärtskompatibilität
            isRead: true,
            repliedAt: new Date().toISOString() // ✅ NEU: Lokaler Zeitstempel
          } : email
        )
      );
      // Statistiken neu laden
      loadEmailCounts();
      console.log('📧 [REPLY-STATUS] Email erfolgreich als beantwortet markiert (isReplied=true).');
    } catch (error) {
      console.error('Fehler beim Markieren als beantwortet:', error);
      setError("Fehler beim Markieren als beantwortet.");
    }
  };

  // Hilfsfunktion zum Laden der vollständigen E-Mail-Daten aus Firestore
  const loadFullEmailData = async (emailId) => {
    if (!userId || !emailId) return null;
    try {
        console.log(`📧 [LOAD-FULL-EMAIL-FIRESTORE] Loading full email data for ID: ${emailId}`);
        const emailDocRef = doc(firestoreDB, 'world_inbox', userId, 'emails', emailId);
        const docSnap = await getDoc(emailDocRef);

        if (docSnap.exists()) {
            const fullEmailData = docSnap.data();
            console.log(`📧 [LOAD-FULL-EMAIL-FIRESTORE] Successfully loaded full email data.`);
            return { id: docSnap.id, ...fullEmailData };
        } else {
            console.warn(`📧 [LOAD-FULL-EMAIL-FIRESTORE] Email ${emailId} not found in world_inbox.`);
            return null;
        }
    } catch (error) {
        console.error(`📧 [LOAD-FULL-EMAIL-FIRESTORE] Error loading email ${emailId}:`, error);
        return null;
    }
  };

  // Funktion zum Öffnen des EmailAiPopup mit Draft-Daten
  const handleOpenDraftInEmailPopup = async (draft) => {
    console.log(`📝 [DRAFT-POPUP] Opening draft in EmailAiPopup:`, {
      draftId: draft.id,
      agentName: draft.agentName,
      originalEmailSubject: draft.originalEmail?.subject
    });

    // Detailliertes Logging der Draft-Daten
    console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Complete originalEmail object from draft:`, draft.originalEmail);
    console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Missing fields check:`, {
      hasBodyText: !!draft.originalEmail?.bodyText,
      hasContent: !!draft.originalEmail?.content,
      hasBody: !!draft.originalEmail?.body,
      hasText: !!draft.originalEmail?.text,
      allFields: Object.keys(draft.originalEmail || {})
    });

    // Versuche die vollständige E-Mail aus der Inbox zu laden
    console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Attempting to load full email data for ID: ${draft.originalEmailId}`);

    // Lade die vollständigen E-Mail-Daten, falls sie in den Draft-Daten fehlen
    let fullOriginalEmail = draft.originalEmail;
    
    // Prüfe, ob wichtige Felder fehlen (bodyText, content, etc.)
    const missingContent = !draft.originalEmail?.bodyText && 
                          !draft.originalEmail?.content && 
                          !draft.originalEmail?.body;
    
    if (missingContent && draft.originalEmailId) {
      console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Content missing, loading full email from database...`);
      
      // Versuche, die E-Mail aus Firestore zu laden
      let loadedEmail = await loadFullEmailData(draft.originalEmailId);
      
      if (loadedEmail) {
        console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Successfully loaded full email data`);
        fullOriginalEmail = {
          ...draft.originalEmail,
          ...loadedEmail,
          // Stelle sicher, dass die wichtigsten Felder gesetzt sind
          bodyText: loadedEmail.bodyText || loadedEmail.content || loadedEmail.body || "",
          content: loadedEmail.content || loadedEmail.bodyText || loadedEmail.body || "",
          body: loadedEmail.body || loadedEmail.bodyText || loadedEmail.content || ""
        };
      } else {
        console.warn(`📝 [DRAFT-ORIGINAL-EMAIL] Could not load full email data for ${draft.originalEmailId}`);
      }
    }

    console.log(`📝 [DRAFT-ORIGINAL-EMAIL] Final originalEmail object:`, {
      hasBodyText: !!fullOriginalEmail?.bodyText,
      hasContent: !!fullOriginalEmail?.content,
      hasBody: !!fullOriginalEmail?.body,
      contentLength: (fullOriginalEmail?.bodyText || fullOriginalEmail?.content || "").length
    });

    // Erstelle ein Objekt mit den Daten, die EmailAiPopup benötigt
    const emailReplyData = {
      id: draft.originalEmailId, // Verwende die ursprüngliche E-Mail-ID
      business_name: extractSenderName(fullOriginalEmail?.from),
      lead_email: extractEmailFromString(fullOriginalEmail?.from),
      lead_description: fullOriginalEmail?.bodyText || fullOriginalEmail?.content || fullOriginalEmail?.body || "",
      lead_website: "",
      lead_instagram: "",
      lead_tiktok: "",
      lead_type: "email_reply",
      // Speichere die ursprüngliche E-Mail für den Kontext (für normale E-Mail-Antworten)
      original_email: {
        subject: fullOriginalEmail?.subject,
        content: fullOriginalEmail?.bodyText || fullOriginalEmail?.content || fullOriginalEmail?.body || "",
        bodyText: fullOriginalEmail?.bodyText || fullOriginalEmail?.content || fullOriginalEmail?.body || "",
        body: fullOriginalEmail?.body || fullOriginalEmail?.bodyText || fullOriginalEmail?.content || "",
        from: fullOriginalEmail?.from,
        date: fullOriginalEmail?.date,
        timestamp: fullOriginalEmail?.date
      },
      // Füge die Draft-Informationen hinzu - WICHTIG: originalEmail muss hier auch gespeichert werden
      draft: {
        id: draft.id,
        content: draft.draftContent,
        agentName: draft.agentName,
        agentId: draft.agentId,
        agentCaseNumber: draft.agentCaseNumber,
        agentTask: draft.agentTask,
        status: draft.status,
        createdAt: draft.createdAt,
        // Füge die originalEmail auch hier hinzu, damit InboxModeEmail.js sie findet
        originalEmail: {
          subject: fullOriginalEmail?.subject,
          content: fullOriginalEmail?.bodyText || fullOriginalEmail?.content || fullOriginalEmail?.body || "",
          bodyText: fullOriginalEmail?.bodyText || fullOriginalEmail?.content || fullOriginalEmail?.body || "",
          body: fullOriginalEmail?.body || fullOriginalEmail?.bodyText || fullOriginalEmail?.content || "",
          from: fullOriginalEmail?.from,
          date: fullOriginalEmail?.date,
          timestamp: fullOriginalEmail?.date
        }
      },
      // Konto-Informationen (falls verfügbar)
      accountEmail: fullOriginalEmail?.accountEmail,
      accountId: fullOriginalEmail?.accountId
    };

    // Detailliertes Logging der übergebenen Daten
    console.log(`📝 [DRAFT-DATA] Complete emailReplyData structure:`, emailReplyData);
    console.log(`📝 [DRAFT-DATA] original_email:`, emailReplyData.original_email);
    console.log(`📝 [DRAFT-DATA] draft.originalEmail:`, emailReplyData.draft.originalEmail);
    console.log(`📝 [DRAFT-DATA] Raw draft object from database:`, draft);

    setDraftEmailData(emailReplyData);
    setShowDraftEmailPopup(true);
  };

  // =================================================================================
  // == SIDE EFFECTS & DATA SYNC
  // =================================================================================

  // Effekt, um zu prüfen, ob eine Migration notwendig ist
  useEffect(() => {
    const checkForOldData = async () => {
      if (!userId) return;

      const legacyPaths = [
        'inbox',
        'spam',
        'sentEmails',
        'forwardedEmails',
        'scheduledEmails',
        'emailReplies',
        'emailLogs',
        'settings/senderEmails'
      ];

      try {
        // Prüfe alle alten RTDB-Pfade parallel
        const checkPromises = legacyPaths.map(path => {
          const oldDataRef = ref(database, `/World/${userId}/${path}`);
          return get(rtdbQuery(oldDataRef, limitToFirst(1)));
        });

        const snapshots = await Promise.all(checkPromises);
        
        // Wenn in einem der Snapshots Daten gefunden werden, ist eine Migration nötig
        const migrationIsNeeded = snapshots.some(snapshot => snapshot.exists());

        if (migrationIsNeeded) {
          const foundPath = legacyPaths.find((path, index) => snapshots[index].exists());
          console.log(`Alte RTDB-Daten in '/World/${userId}/${foundPath}' gefunden. Migration erforderlich.`);
          setNeedsMigration(true);
          setLoading(false); 
        } else {
          console.log('Keine alten RTDB-Daten gefunden. Lade aus Firestore.');
          if (emails.length === 0) {
              loadEmailsPage(1, null, 'all');
              loadEmailCounts();
          }
        }
      } catch (error) {
        console.error("Fehler bei der Prüfung auf alte RTDB-Daten:", error);
        setLoading(false);
        setError("Fehler bei der Prüfung auf notwendige Datenmigration.");
      }
    };
    
    if (!needsMigration) {
       checkForOldData();
    }
  }, [userId, needsMigration, loadEmailCounts, loadEmailsPage]);

  // NEU: Funktion zum Starten der Migration
  const handleStartMigration = async () => {
    setIsMigrating(true);
    setMigrationMessage('Migration starts... This process can take a few minutes. Please do not close the window.');
    try {
      const result = await migrateInboxToFirestore({});
      setMigrationMessage(result.data.message || 'Migration successful! The page will reload.');
      console.log('Migration result:', result.data);
      
      // Nach erfolgreicher Migration die Seite neu laden, um die neuen Daten aus Firestore zu laden
      setTimeout(() => {
        window.location.reload();
      }, 3000);

    } catch (error) {
      console.error("Error during migration:", error);
      setMigrationMessage(`An error occurred: ${error.message}. Please try again or contact support.`);
      setIsMigrating(false);
    }
  };

  // ✅ REMOVED: Client-side filtering - now done server-side in Firestore
  // filteredEmails will now be set directly from each email type's state

  // ✅ SIMPLIFIED: Direct email state assignment based on account type
  const filteredEmails = useMemo(() => {
    switch (selectedAccount) {
      case 'sent':
        return sentEmails;
      case 'replied':
        return repliedEmails;
      case 'deleted':
        return deletedEmails;
      case 'forwarded':
        return forwardedEmails;
      case 'scheduled':
        return scheduledEmails;
      case 'safeguard':
        return safeGuardDrafts;
      default:
        return emails; // Inbox emails (already server-side filtered)
    }
  }, [emails, sentEmails, repliedEmails, deletedEmails, forwardedEmails, scheduledEmails, safeGuardDrafts, selectedAccount]);

  // 🚀 OPTIMIZED: Memoized calculations for better performance (only for inbox)
  const enthusiasmStatsCalculated = useMemo(() => {
    // Only calculate for inbox emails (others don't have enthusiasm scores)
    if (selectedAccount !== 'all' && selectedAccount !== 'scheduled' && !emailAccounts.some(acc => acc.id === selectedAccount)) {
      return { low: 0, medium: 0, high: 0, unknown: 0 };
    }

    if (!emails || emails.length === 0) {
      return { low: 0, medium: 0, high: 0, unknown: 0 };
    }

    let low = 0, medium = 0, high = 0, unknown = 0;

    emails.forEach(email => {
      if (email.enthusiasmScore === undefined) {
        unknown++;
      } else if (email.enthusiasmScore >= 1 && email.enthusiasmScore <= 3) {
        low++;
      } else if (email.enthusiasmScore >= 4 && email.enthusiasmScore <= 6) {
        medium++;
      } else if (email.enthusiasmScore >= 7 && email.enthusiasmScore <= 10) {
        high++;
      } else {
        unknown++;
      }
    });

    return { low, medium, high, unknown };
  }, [emails, selectedAccount, emailAccounts]);

  // 🔥 Central Effect for Account-Switching AND Server-Side Filter Changes
  useEffect(() => {
    if (!userId || isMigrating || needsMigration) return;
    
    const accountChanged = previousAccount !== selectedAccount;
    const isInboxAccount = selectedAccount === 'all' || (!['sent', 'replied', 'deleted', 'forwarded', 'scheduled'].includes(selectedAccount));
    
    if (accountChanged) {
      console.log(`🔄 Account switched: ${previousAccount} → ${selectedAccount}`);
      
      // Load counts only on account change
      loadEmailCounts();
      if (aiTags.length > 0) {
        loadTagCounts(selectedAccount);
      }
      
      setPreviousAccount(selectedAccount);
    }

    // Reload inbox emails when account changes OR when filters change (server-side filtering)
    if (isInboxAccount && (accountChanged || !accountChanged)) {
      console.log(`🔥 Loading ${accountChanged ? 'new account' : 'filtered'} emails: account=${selectedAccount}, enthusiasm=${enthusiasmFilter}, tag=${selectedTag}`);
      loadEmailsPage(1, null, selectedAccount);
    }
    
  }, [selectedAccount, userId, isMigrating, needsMigration, previousAccount, aiTags.length, enthusiasmFilter, selectedTag, loadEmailCounts, loadTagCounts, loadEmailsPage]);

  // NEU: Lade E-Mail-Konten aus Firestore
  useEffect(() => {
    if (!userId || isMigrating) return;

    // Pfad zu den E-Mail-Konten in Firestore (Annahme)
    const accountsCollectionRef = collection(firestoreDB, 'world_users', userId, 'emailAccounts');
    const q = query(accountsCollectionRef, orderBy('email')); // Annahme: Sortierung nach E-Mail-Adresse
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const accounts = [];
        querySnapshot.forEach((doc) => {
          accounts.push({
                id: doc.id,
                ...doc.data()
          });
        });
        setEmailAccounts(accounts);
        console.log(`[FIRESTORE] ${accounts.length} E-Mail-Konten geladen.`);
    }, (error) => {
        console.error("Fehler beim Laden der E-Mail-Konten aus Firestore:", error);
        setError("E-Mail-Konten konnten nicht geladen werden.");
    });

    return () => unsubscribe();
  }, [userId, firestoreDB, isMigrating]);

  // Removed: Now handled in central account switching effect

  // NEU: Lade weitergeleitete E-Mails aus Firestore, nur wenn die Ansicht aktiv ist
  useEffect(() => {
    if (!userId || isMigrating || selectedAccount !== 'forwarded') {
      if (selectedAccount === 'forwarded') setForwardedEmails([]);
      return;
    }

    console.log('Lade weitergeleitete E-Mails aus Firestore...');
    setLoading(true);

    // Annahme: Es gibt eine 'world_forwarded' Collection, analog zu 'world_sent'
    const forwardedCollectionRef = collection(firestoreDB, 'world_forwarded', userId, 'emails');
    const q = query(forwardedCollectionRef, orderBy('timestamp', 'desc'), limit(50));

    getDocs(q)
        .then(snapshot => {
            const forwardedData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
          isForwarded: true,
            }));
            setForwardedEmails(forwardedData);
            console.log(`${forwardedData.length} weitergeleitete E-Mails aus Firestore geladen.`);
        })
        .catch(error => {
            console.error("Fehler beim Laden weitergeleiteter E-Mails aus Firestore:", error);
            setError("Fehler beim Laden der weitergeleiteten E-Mails.");
        })
        .finally(() => setLoading(false));
  }, [userId, firestoreDB, isMigrating, selectedAccount]);

  // NEU: Lade geplante E-Mails aus Firestore, nur wenn die Ansicht aktiv ist
  useEffect(() => {
    if (!userId || isMigrating || selectedAccount !== 'scheduled') {
      if (selectedAccount === 'scheduled') setScheduledEmails([]);
      return;
    }

    console.log('Lade geplante E-Mails aus Firestore...');
    setLoading(true);

    const scheduledCollectionRef = collection(firestoreDB, 'world_scheduled_emails', userId, 'emails');
    const q = query(scheduledCollectionRef, orderBy('originalScheduledTime', 'desc'), limit(50)); // Sortierung nach ursprünglich geplanter Zeit (neueste zuerst)

    getDocs(q)
        .then(snapshot => {
            const scheduledData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                isScheduled: true,
            }));
            setScheduledEmails(scheduledData);
            console.log(`${scheduledData.length} geplante E-Mails aus Firestore geladen.`);
        })
        .catch(error => {
            console.error("Fehler beim Laden geplanter E-Mails aus Firestore:", error);
            setError("Fehler beim Laden der geplanten E-Mails.");
        })
        .finally(() => setLoading(false));
  }, [userId, firestoreDB, isMigrating, selectedAccount]);

  // NEU: Lade SafeGuard Drafts aus Firestore, nur wenn die Ansicht aktiv ist
  useEffect(() => {
    if (!userId || isMigrating || selectedAccount !== 'safeguard') {
      if (selectedAccount === 'safeguard') setSafeGuardDrafts([]);
      return;
    }

    console.log('🛡️ [SAFEGUARD] Lade SafeGuard Drafts aus Firestore...');
    setLoading(true);

    const safeGuardDraftsRef = collection(firestoreDB, 'world_drafts', userId, 'emails');
    const q = query(
      safeGuardDraftsRef,
      where('status', '==', 'draft'),
      where('reason', '==', 'safeguard_rejection'),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    getDocs(q)
        .then(snapshot => {
            const safeGuardData = snapshot.docs.map(doc => {
              const data = doc.data();
              return {
                id: doc.id,
                ...data,
                isSafeGuardDraft: true,
                // Stell sicher, dass das Zeitstempel-Format einheitlich ist
                timestamp: data.timestamp?.toMillis?.() || Date.now(),
                date: data.date || new Date().toISOString()
              };
            });
            setSafeGuardDrafts(safeGuardData);
            console.log(`🛡️ [SAFEGUARD] ${safeGuardData.length} SafeGuard Drafts aus Firestore geladen.`);
        })
        .catch(error => {
            console.error("🛡️ [SAFEGUARD] Fehler beim Laden der SafeGuard Drafts aus Firestore:", error);
            setError("Fehler beim Laden der SafeGuard Drafts.");
        })
        .finally(() => setLoading(false));
  }, [userId, firestoreDB, isMigrating, selectedAccount]);

  // Initialize data loading (optimiert)
  useEffect(() => {
    if (!userId || needsMigration || isMigrating) return;

    // Load settings only once
    const loadSettings = async () => {
      try {
        const [inboxActiveSnapshot, autoProcessSnapshot] = await Promise.all([
          get(ref(database, `/World/${userId}/settings/inboxActive`)),
          get(ref(database, `/World/${userId}/settings/autoProcessEmails`))
        ]);
        
        setInboxActive(inboxActiveSnapshot.exists() ? inboxActiveSnapshot.val() : false);
        setAutoProcessEmails(autoProcessSnapshot.exists() ? autoProcessSnapshot.val() : false);
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };

    loadSettings();
    
    // Initial data load - nur wenn noch keine E-Mails geladen sind
    if (emails.length === 0) {
      loadEmailCounts();
      loadEmailsPage(1, null, 'all');
    }

  }, [userId, needsMigration, isMigrating]); // Simplified dependencies

  // Lade AI Tags (Settings - bleibt vorerst in RTDB)
  useEffect(() => {
    if (!userId) return;

    const aiTagsRef = ref(database, `/World/${userId}/settings/aiTags`);
    
    const unsubscribe = onValue(aiTagsRef, (snapshot) => {
      if (snapshot.exists()) {
        const tagsData = snapshot.val();
        const tagsArray = Object.entries(tagsData).map(([id, tag]) => ({
          id,
          ...tag
        }));
        setAiTags(tagsArray);
        console.log(`${tagsArray.length} AI tags loaded`);
      } else {
        setAiTags([]);
      }
    });

    return () => unsubscribe();
  }, [userId]);

  // Removed: Tag counts now loaded in central account switching effect

  // Filtere replied emails aus sent emails
  useEffect(() => {
    const replied = sentEmails.filter(email => 
      email.emailType === 'custom_reply' || email.emailType === 'draft_reply'
    );
    setRepliedEmails(replied);
    // console.log(`${replied.length} replied emails found from ${sentEmails.length} sent emails`);
  }, [sentEmails]);

  // Removed: Moved up before enthusiasm calculations

  // ✅ REMOVED: No longer needed since we use server-side filtering
  // useEffect for setFilteredEmails removed - using useMemo filteredEmails directly

  // Removed complex function - using simple pattern like scheduled

  // Funktion zum Aktualisieren des inboxActive-Status
  const handleToggleInboxActive = () => {
    if (!userId) return;

    const newStatus = !inboxActive;
    setInboxActive(newStatus);
    
    // Speichere den neuen Status in der Datenbank
    const inboxActiveRef = ref(database, `/World/${userId}/settings/inboxActive`);
    set(inboxActiveRef, newStatus)
      .then(() => {
        console.log(`Smart Inbox status set to ${newStatus ? 'active' : 'inactive'}`);
        if (newStatus) {
          // Wenn aktiviert, sofort nach neuen E-Mails suchen
          handleRefresh();
        }
      })
      .catch((error) => {
        console.error('Error saving inboxActive status:', error);
        setInboxActive(!newStatus); // Zurücksetzen bei Fehler
      });
  };

  // Funktion zum Wechseln des ausgewählten Kontos (simple)
  const handleAccountChange = (accountId) => {
    if (accountId === selectedAccount) return; // No change needed
    
    console.log(`🔄 ACCOUNT SWITCH: User selected ${accountId}`);
    
    // Clear states (useEffects will reload data)
    setEmails([]);
    setLastEmailDoc(null);
    setCurrentPage(1);
    setHasMoreEmails(true);
    setSentEmails([]);
    setDeletedEmails([]);
    setSafeGuardDrafts([]);
    setHasMoreSentEmails(false);
    setHasMoreDeletedEmails(false);
    
    // Update selected account - this will trigger the useEffect above
    setSelectedAccount(accountId);
  };

  // Funktion zum Ändern des Tag-Filters
  const handleTagChange = (tagValue) => {
    setSelectedTag(tagValue);
  };

  // Funktion zum Ändern des Enthusiasmus-Filters
  const handleEnthusiasmFilterChange = (filterValue) => {
    setEnthusiasmFilter(filterValue);
  };

  // Berechnet die Begeisterungsgrad-Statistiken für die E-Mails
  const calculateEnthusiasmStats = (emailList) => {
    if (!emailList || emailList.length === 0) {
      return { low: 0, medium: 0, high: 0, unknown: 0 };
    }

    let low = 0;
    let medium = 0;
    let high = 0;
    let unknown = 0;

    emailList.forEach(email => {
      if (email.enthusiasmScore === undefined) {
        unknown++;
      } else if (email.enthusiasmScore >= 1 && email.enthusiasmScore <= 3) {
        low++;
      } else if (email.enthusiasmScore >= 4 && email.enthusiasmScore <= 6) {
        medium++;
      } else if (email.enthusiasmScore >= 7 && email.enthusiasmScore <= 10) {
        high++;
      } else {
        unknown++;
      }
    });

    return {
      low,
      medium,
      high,
      unknown
    };
  };

  // Funktion zum manuellen Verarbeiten einer E-Mail mit Agents
  const handleProcessEmailWithAgents = async (emailId, isSpam = false) => {
    try {
      console.log(`🤖 [MANUAL-PROCESSING] Processing email ${emailId} with agents...`);
      const result = await processEmailWithAgents({ emailId, isSpam });
      console.log(`🤖 [MANUAL-PROCESSING] Result:`, result.data);
      
      if (result.data.success) {
        if (result.data.action === 'draft_created') {
          console.log(`📝 [DRAFT-CREATED] Draft ${result.data.draftId} created by agent ${result.data.agentUsed.name}`);
        } else if (result.data.action === 'classified_no_reply') {
          console.log(`🏷️ [CLASSIFIED] Email classified by agent ${result.data.agentUsed.name} - no reply needed`);
        }
      }
    } catch (error) {
      console.error(`🤖 [MANUAL-PROCESSING] Error processing email ${emailId}:`, error);
    }
  };

  // Callback für das Schließen des Draft EmailAiPopup
  const handleDraftEmailPopupClose = (emailSent = false) => {
    setShowDraftEmailPopup(false);
    
    if (emailSent) {
      console.log(`📝 [DRAFT-POPUP] Email sent successfully from draft`);
      
      // Entferne den Draft sofort aus der EmailDraftsPreview (optimistische UI-Aktualisierung)
      if (draftEmailData && draftEmailData.draft && window.emailDraftsPreview && window.emailDraftsPreview.markDraftAsSending) {
        window.emailDraftsPreview.markDraftAsSending(draftEmailData.draft.id);
      }
    }
    
    setDraftEmailData(null);
  };

  // Handler für Draft Bulk Mode
  const handleDraftBulkMode = (drafts, selectedIndex = 0) => {
    setBulkDrafts(drafts);
    setSelectedDraftIndex(selectedIndex);
    setShowDraftBulkMode(true);
  };

  // Callback für das Schließen des Draft Bulk Mode
  const handleDraftBulkModeClose = () => {
    setShowDraftBulkMode(false);
    setBulkDrafts([]);
  };

  // Lade User Settings, AI Settings, Topics und Sender Emails für den Bulk Mode
  useEffect(() => {
    if (!userId) return;

    // Lade User Settings
    const userSettingsRef = ref(database, `/World/${userId}/settings`);
    const unsubscribeUserSettings = onValue(userSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserSettings(snapshot.val());
      }
    });

    // Lade AI Settings
    const aiSettingsRef = ref(database, `/World/${userId}/aiSettings`);
    const unsubscribeAiSettings = onValue(aiSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        setAiSettings(snapshot.val());
      }
    });

    // Lade User Topics
    const userTopicsRef = ref(database, `/World/${userId}/topics`);
    const unsubscribeUserTopics = onValue(userTopicsRef, (snapshot) => {
      if (snapshot.exists()) {
        setUserTopics(snapshot.val());
        setIsTopicsLoaded(true);
      } else {
        setUserTopics({});
        setIsTopicsLoaded(true);
      }
    });

    // Topic Type Mapping
    setTopicTypeMapping({
      "Introduce Yourself": "intro",
      "Follow Up": "follow_up_1", 
      "Second Follow Up": "follow_up_2",
      "Third Follow Up": "follow_up_3",
      "Reply To Mail": "custom_reply"
    });

    // Lade Footer Settings separat und füge sie zu den userSettings hinzu
    const footerSettingsRef = ref(database, `/World/${userId}/settings/footer`);
    const unsubscribeFooterSettings = onValue(footerSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const footerData = snapshot.val();
        console.log(`🦶 [SMARTINBOX] Footer settings loaded:`, footerData);
        
        // Füge Footer-Daten zu den userSettings hinzu
        setUserSettings(prev => ({
          ...prev,
          footer_text: footerData.footer_text,
          footer_image_url: footerData.footer_image_url,
          footer_image_position: footerData.footer_image_position,
          footer_font: footerData.footer_font,
          footer_image_size: footerData.footer_image_size
        }));
      } else {
        console.log(`🦶 [SMARTINBOX] No footer settings found`);
      }
    });

    return () => {
      unsubscribeUserSettings();
      unsubscribeAiSettings();
      unsubscribeUserTopics();
      unsubscribeFooterSettings();
    };
  }, [userId]);

  // Removed: Now handled in central account switching effect

  // Function to analyze an email
  const handleAnalyzeEmail = async (emailId, isSpam = false) => {
    try {
      console.log(`🔍 [ANALYZE] Analyzing email ${emailId} (Spam: ${isSpam})...`);
      const result = await analyzeEmailEndpoint({ emailId, isSpam });
      console.log(`🔍 [ANALYZE] Analysis result:`, result.data);
      
      // Update the email in local state with new tags
      if (result.data && result.data.aiTags) {
        setEmails(prevEmails => prevEmails.map(email => {
          if (email.id === emailId) {
            return {
              ...email,
              aiTags: result.data.aiTags,
              analysis: {
                ...email.analysis,
                aiTags: result.data.aiTags
              }
            };
          }
          return email;
        }));
      }
      
    } catch (error) {
      console.error(`❌ [ANALYZE] Error analyzing email ${emailId}:`, error);
    }
  };

  // Migrations-Ansicht: Wenn eine Migration notwendig ist, zeige den Button statt der Inbox
  if (needsMigration) {
    return (
      <div className="smart-inbox-container migration-needed">
        <div className="smartInbox_migration_modal">
          <div className="smartInbox_migration_icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 15V9L12 4L20 9V15L12 20L4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 12L20 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 12V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 12L4 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="smartInbox_migration_title">Database Migration Required</h2>
          <div className="smartInbox_migration_content">
            <p className="smartInbox_migration_description">
              We've upgraded our database technology to make your inbox faster and more efficient.
            </p>
            <p className="smartInbox_migration_instruction">
              To continue, your existing email data needs to be migrated to the new format once.
            </p>
          </div>
          <button 
            onClick={handleStartMigration} 
            disabled={isMigrating} 
            className="smartInbox_migration_button"
          >
            {isMigrating ? (
              <>
                <div className="migration_spinner"></div>
                Migration in Progress...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Migrate to New Inbox
              </>
            )}
          </button>
          {migrationMessage && (
            <div className="smartInbox_migration_message">
              {migrationMessage}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="smart-inbox-container">
      {/* Header: Title, Refresh Button etc. */}
      <div className="smartInbox_header">
        <div className="smartInbox_header_left">
          <h2>Inbox</h2>
        </div>
        <div className="smartInbox_stats">
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.total}</span>
            <span className="smartInbox_stats_label">Total</span>
          </div>
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.unread}</span>
            <span className="smartInbox_stats_label">Unread</span>
          </div>
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.spam}</span>
            <span className="smartInbox_stats_label">Spam</span>
          </div>
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.scheduled}</span>
            <span className="smartInbox_stats_label">Scheduled</span>
          </div>
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.safeguard}</span>
            <span className="smartInbox_stats_label">SafeGuard</span>
          </div>
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.sent}</span>
            <span className="smartInbox_stats_label">Sent</span>
          </div>
          <div className="smartInbox_stats_item">
            <span className="smartInbox_stats_value">{stats.deleted}</span>
            <span className="smartInbox_stats_label">Deleted</span>
          </div>
        </div>
        <div className="smartInbox_buttons">
          {/* Toggle Switches */}
          <div className="smartInbox_header_toggles">
            {selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'forwarded' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && selectedAccount !== 'safeguard' && stats.total > 0 && (
              <div className="smartInbox_header_toggle">
                <span className="toggle_label">Analytics</span>
                <div 
                  className={`smartInbox_toggle_switch ${showEnthusiasmDistribution ? 'active' : ''}`}
                  onClick={() => setShowEnthusiasmDistribution(v => !v)}
                >
                  <div className="smartInbox_toggle_slider"></div>
                </div>
              </div>
            )}
            
            {/* Filters Toggle - immer anzeigen wenn E-Mails vorhanden sind oder wenn scheduled/sent/replied/forwarded/deleted/safeguard ausgewählt ist */}
            {(filteredEmails.length > 0 || selectedAccount === 'sent' || selectedAccount === 'replied' || selectedAccount === 'forwarded' || selectedAccount === 'deleted' || selectedAccount === 'scheduled' || selectedAccount === 'safeguard') && (
              <div className="smartInbox_header_toggle">
                <span className="toggle_label">Filters</span>
                <div 
                  className={`smartInbox_toggle_switch ${showMoreFilters ? 'active' : ''}`}
                  onClick={() => setShowMoreFilters(v => !v)}
                >
                  <div className="smartInbox_toggle_slider"></div>
                </div>
              </div>
            )}
          </div>
          
          <button 
            className={`smartInbox_button ${refreshing ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
            style={{backgroundColor: 'transparent', color: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'}}
          >
            {!refreshing && <img style={{width: '14px', marginRight: '5px'}} src="/img/refresh-arrow.svg" alt="Refresh" />}
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Begeisterungsgrad-Verteilung - nur anzeigen, wenn nicht "Sent Emails", "Replied Emails", "Deleted Emails", "Scheduled Emails" oder "SafeGuard" ausgewählt ist */}
      {selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && selectedAccount !== 'safeguard' && stats.total > 0 && showEnthusiasmDistribution ? (
        <div className="smartInbox_enthusiasm_distribution">
          <div className="smartInbox_enthusiasm_label">Enthusiasm Distribution:</div>
          <div className="smartInbox_enthusiasm_bar">
            {enthusiasmStats.low > 0 && (
              <div 
                className="smartInbox_enthusiasm_segment smartInbox_enthusiasm_low" 
                style={{ width: `${(enthusiasmStats.low / stats.total) * 100}%` }}
                title={`Low (1-3): ${enthusiasmStats.low} emails (${Math.round((enthusiasmStats.low / stats.total) * 100)}%)`}
              >
                {enthusiasmStats.low > stats.total * 0.05 && `${Math.round((enthusiasmStats.low / stats.total) * 100)}%`}
              </div>
            )}
            {enthusiasmStats.medium > 0 && (
              <div 
                className="smartInbox_enthusiasm_segment smartInbox_enthusiasm_medium" 
                style={{ width: `${(enthusiasmStats.medium / stats.total) * 100}%` }}
                title={`Medium (4-6): ${enthusiasmStats.medium} emails (${Math.round((enthusiasmStats.medium / stats.total) * 100)}%)`}
              >
                {enthusiasmStats.medium > stats.total * 0.05 && `${Math.round((enthusiasmStats.medium / stats.total) * 100)}%`}
              </div>
            )}
            {enthusiasmStats.high > 0 && (
              <div 
                className="smartInbox_enthusiasm_segment smartInbox_enthusiasm_high" 
                style={{ width: `${(enthusiasmStats.high / stats.total) * 100}%` }}
                title={`High (7-10): ${enthusiasmStats.high} emails (${Math.round((enthusiasmStats.high / stats.total) * 100)}%)`}
              >
                {enthusiasmStats.high > stats.total * 0.05 && `${Math.round((enthusiasmStats.high / stats.total) * 100)}%`}
              </div>
            )}
            {enthusiasmStats.unknown > 0 && (
              <div 
                className="smartInbox_enthusiasm_segment smartInbox_enthusiasm_unknown" 
                style={{ width: `${(enthusiasmStats.unknown / stats.total) * 100}%` }}
                title={`Unknown: ${enthusiasmStats.unknown} emails (${Math.round((enthusiasmStats.unknown / stats.total) * 100)}%)`}
              >
                {enthusiasmStats.unknown > stats.total * 0.05 && `${Math.round((enthusiasmStats.unknown / stats.total) * 100)}%`}
              </div>
            )}
          </div>
          <div className="smartInbox_enthusiasm_legend">
            <div className="smartInbox_enthusiasm_legend_item">
              <span className="smartInbox_enthusiasm_legend_color smartInbox_enthusiasm_low"></span>
              <span className="smartInbox_enthusiasm_legend_text">Low (1-3)</span>
            </div>
            <div className="smartInbox_enthusiasm_legend_item">
              <span className="smartInbox_enthusiasm_legend_color smartInbox_enthusiasm_medium"></span>
              <span className="smartInbox_enthusiasm_legend_text">Medium (4-6)</span>
            </div>
            <div className="smartInbox_enthusiasm_legend_item">
              <span className="smartInbox_enthusiasm_legend_color smartInbox_enthusiasm_high"></span>
              <span className="smartInbox_enthusiasm_legend_text">High (7-10)</span>
            </div>
            {enthusiasmStats.unknown > 0 && (
              <div className="smartInbox_enthusiasm_legend_item">
                <span className="smartInbox_enthusiasm_legend_color smartInbox_enthusiasm_unknown"></span>
                <span className="smartInbox_enthusiasm_legend_text">Unknown</span>
              </div>
            )}
          </div>
        </div>
      ) : selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && selectedAccount !== 'safeguard' && stats.total > 0 && !showEnthusiasmDistribution ? null : selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && selectedAccount !== 'safeguard' && stats.total > 0 ? (
        <div className="smartInbox_feature_locked">
          <div className="smartInbox_lock_icon">
            <span className="smartInbox_lock_circle">
              <span className="smartInbox_lock_symbol">
                <img style={{width: '28px', marginTop: '5px', filter: 'invert(1)'}} src="/img/lock.svg" alt="Lock" />
              </span>
            </span>
          </div>
          <h3 className="smartInbox_locked_title">Analytics Feature Locked</h3>
          <p className="smartInbox_locked_message">
            Generate at least 100 Replies to show this feature
          </p>
          <div className="smartInbox_progress_container">
            <div className="smartInbox_progress_bar">
              <div 
                className="smartInbox_progress_fill" 
                style={{ width: `${Math.min(stats.total / 100 * 100, 100)}%` }}
              ></div>
            </div>
            <div className="smartInbox_progress_text">
              {stats.total}/100 Emails
            </div>
          </div>
        </div>
      ) : null}

      {/* Tag Distribution - nur anzeigen wenn nicht "Sent Emails", "Deleted Emails", "Scheduled Emails" oder "SafeGuard" ausgewählt ist */}
      {selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && selectedAccount !== 'safeguard' && stats.total > 0 && showEnthusiasmDistribution && aiTags.length > 0 ? (
        <div className="smartInbox_enthusiasm_distribution">
          <div className="smartInbox_tag_distribution">
            <div className="smartInbox_enthusiasm_label">Tag Distribution:</div>
            <div className="smartInbox_enthusiasm_bar">
              {aiTags.map(tag => {
                // NEU: Verwende die aus Firestore geladenen Zählungen
                const emailsWithTag = tagCounts[tag.tag] || 0;
                
                // Berechne den Prozentsatz
                const totalRelevantEmails = stats.total - stats.spam;
                const percentage = totalRelevantEmails > 0 ? (emailsWithTag / totalRelevantEmails) * 100 : 0;
                
                // Nur anzeigen wenn es E-Mails mit diesem Tag gibt
                return emailsWithTag > 0 ? (
                  <div 
                    key={tag.id}
                    className="smartInbox_enthusiasm_segment" 
                    style={{ 
                      width: `${percentage}%`,
                      backgroundColor: tag.color || '#4a90e2'
                    }}
                    title={`${tag.name}: ${emailsWithTag} emails (${Math.round(percentage)}%)`}
                  >
                    {percentage > 5 && `${Math.round(percentage)}%`}
                  </div>
                ) : null;
              })}
            </div>
            <div className="smartInbox_enthusiasm_legend">
              {aiTags.map(tag => {
                // NEU: Verwende die aus Firestore geladenen Zählungen
                const emailsWithTag = tagCounts[tag.tag] || 0;
                
                // Nur in der Legende anzeigen wenn es E-Mails mit diesem Tag gibt
                return emailsWithTag > 0 ? (
                  <div key={tag.id} className="smartInbox_enthusiasm_legend_item">
                    <span 
                      className="smartInbox_enthusiasm_legend_color" 
                      style={{ backgroundColor: tag.color || '#4a90e2' }}
                    ></span>
                    <span className="smartInbox_enthusiasm_legend_text">
                      {tag.name} ({emailsWithTag})
                    </span>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        </div>
      ) : null}

      {/* Inbox Filters Component */}
      <InboxFilters 
        selectedAccount={selectedAccount}
        onAccountChange={handleAccountChange}
        emailAccounts={emailAccounts}
        formatEmailAddress={formatEmailAddress}
        showMoreFilters={showMoreFilters}
        selectedTag={selectedTag}
        onTagChange={handleTagChange}
        aiTags={aiTags}
        tagCounts={tagCounts}
        enthusiasmFilter={enthusiasmFilter}
        onEnthusiasmFilterChange={handleEnthusiasmFilterChange}
      />

      {/* Email Drafts Preview - nur anzeigen, wenn nicht "Sent Emails", "Replied Emails", "Forwarded Emails", "Deleted Emails", "Scheduled Emails" oder "SafeGuard" ausgewählt ist */}
      {selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'forwarded' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && selectedAccount !== 'safeguard' && (
        <EmailDraftsPreview 
          userId={userId} 
          onOpenDraftInEmailPopup={handleOpenDraftInEmailPopup}
          onOpenDraftBulkMode={handleDraftBulkMode}
        />
      )}

      {error && error.includes('Info:') ? (
        <div className="smartInbox_info_message">
          <p>{error.replace('Info: ', '')}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      ) : error ? (
        <div className="smartInbox_error">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Close</button>
        </div>
      ) : null}

      {loading ? (
        <div className="smartInbox_loading">
          <img src="/img/salesgod_dark.webp" alt="SalesGod" className="smartInbox_spinner" />
          <p>Loading emails...</p>
        </div>
      ) : filteredEmails.length === 0 && selectedAccount !== 'scheduled' ? (
        <div className="smartInbox_empty">
          <div className="smartInbox_empty_illustration">
            <img 
              src="/img/mailbox-empty.svg" 
              alt="Empty Inbox" 
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/img/mailerle.webp";  // Fallback auf existierendes Bild
              }}
              style={{ maxWidth: "80px", maxHeight: "80px" }}
            />
          </div>
          <h3 className="smartInbox_empty_title">
            {selectedAccount === 'sent'
              ? "No sent emails found"
              : selectedAccount === 'replied'
                ? "No replied emails found"
                : selectedAccount === 'forwarded'
                  ? "No forwarded emails found"
                  : selectedAccount === 'deleted'
                    ? "No deleted emails found"
                    : selectedAccount === 'scheduled'
                      ? "No scheduled emails found"
                    : selectedAccount === 'safeguard'
                      ? "No SafeGuard drafts found"
                    : selectedAccount === 'all'
                      ? "No emails found"
                      : `No emails found for this account`}
          </h3>
          
          {/* Nur Smart Inbox Aktivierung anzeigen, wenn noch keine E-Mails in der DB sind */}
          {selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'forwarded' && selectedAccount !== 'deleted' && selectedAccount !== 'safeguard' && emails.length === 0 && (
            <div className="smartInbox_activation">
              <div className="smartInbox_toggle_container">
                <div className="smartInbox_toggle_header">
                  <h4 className="smartInbox_toggle_title">Smart Inbox</h4>
                  <div className={`smartInbox_toggle ${inboxActive ? 'active' : ''}`} onClick={handleToggleInboxActive}>
                    <div className="smartInbox_toggle_switch_2"></div>
                  </div>
                </div>
                <p className="smartInbox_toggle_description">
                  {inboxActive 
                    ? "Smart Inbox is active - emails are being retrieved automatically" 
                    : "Step 1: Activate Smart Inbox to enable automatic email retrieval"}
                </p>
                <p className="smartInbox_privacy_note">
                  {inboxActive 
                    ? "Your email data is securely stored on BonusClub's servers to power smart features."
                    : "By activating Smart Inbox, you agree that your email data will be securely stored on BonusClub's servers to enable our smart features and improve your experience."}
                </p>
              </div>
            </div>
          )}
          
          <button 
            className="smartInbox_refresh_button" 
            onClick={handleRefresh} 
            disabled={refreshing || (emails.length === 0 && !inboxActive)}
          >
            {refreshing ? (
              <>
                <span className="refresh_spinner"></span>
                <span>Refreshing...</span>
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 8.00001C14 11.3137 11.3137 14 8.00001 14C4.68629 14 2.00001 11.3137 2.00001 8.00001C2.00001 4.68629 4.68629 2.00001 8.00001 2.00001C9.88652 2.00001 11.5695 2.90844 12.6003 4.27446" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M14 3.50001V8.00001H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>{emails.length === 0 && !inboxActive ? "Activate Smart Inbox First" : "Refresh Inbox"}</span>
              </>
            )}
          </button>
        </div>
      ) : selectedAccount === 'scheduled' ? (
        <ScheduledEmails userId={userId} />
      ) : (
        <div>
          {/* Titel für spezielle E-Mail-Ansichten */}
          {(selectedAccount === 'sent' || selectedAccount === 'replied' || selectedAccount === 'forwarded' || selectedAccount === 'deleted' || selectedAccount === 'scheduled' || selectedAccount === 'safeguard') && (
            <div className={`smartInbox_section_header smartInbox_section_header_${selectedAccount}`}>
              <h3 className="smartInbox_section_title">
                {selectedAccount === 'sent' && (
                  <>
                    Sent Emails
                    <div className="smartInbox_section_count smartInbox_section_count_sent">
                      {filteredEmails.length}
                    </div>
                  </>
                )}
                {selectedAccount === 'replied' && (
                  <>
                    Replied Emails
                    <div className="smartInbox_section_count smartInbox_section_count_replied">
                      {filteredEmails.length}
                    </div>
                  </>
                )}
                {selectedAccount === 'forwarded' && (
                  <>
                    Forwarded Emails
                    <div className="smartInbox_section_count smartInbox_section_count_forwarded">
                      {filteredEmails.length}
                    </div>
                  </>
                )}
                {selectedAccount === 'deleted' && (
                  <>
                    Deleted Emails
                    <div className="smartInbox_section_count smartInbox_section_count_deleted">
                      {filteredEmails.length}
                    </div>
                  </>
                )}
                {selectedAccount === 'safeguard' && (
                  <>
                    🛡️ SafeGuard Drafts
                    <div className="smartInbox_section_count smartInbox_section_count_safeguard">
                      {filteredEmails.length}
                    </div>
                  </>
                )}
                {selectedAccount === 'scheduled' && 'Scheduled Emails'}
              </h3>
            </div>
          )}
          
          <div className="smartInbox_emails">
            {filteredEmails.map((email) => (
              <SmartInboxEmail
                key={email.id}
                email={email}
                userId={userId}
                emailType={selectedAccount === 'sent' ? 'sent' : selectedAccount === 'replied' ? 'replied' : selectedAccount === 'forwarded' ? 'forwarded' : selectedAccount === 'deleted' ? 'deleted' : selectedAccount === 'scheduled' ? 'scheduled' : selectedAccount === 'safeguard' ? 'safeguard' : 'inbox'}
                onDelete={handleDeleteEmail}
                onMarkAsReplied={handleMarkAsReplied}
                onProcessWithAgents={handleProcessEmailWithAgents}
                autoProcessEnabled={aiSettings.autoProcessEmails}
              />
            ))}
            
            {/* Load More Button (nur für Inbox) */}
            {(selectedAccount === 'all' || (selectedAccount !== 'sent' && selectedAccount !== 'replied' && selectedAccount !== 'forwarded' && selectedAccount !== 'deleted' && selectedAccount !== 'scheduled' && selectedAccount !== 'safeguard')) && hasMoreEmails && (
              <div className="smartInbox_load_more_container">
                <button 
                  className="smartInbox_load_more_button"
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <>
                      <span className="load_more_spinner"></span>
                      <span>Loading more emails...</span>
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 4v8m-4-4h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      <span>Load More</span>
                    </>
                  )}
                </button>
                <p className="smartInbox_pagination_info">
                  Showing {filteredEmails.length} emails • Page {currentPage}
                </p>
              </div>
            )}

            {/* Load More Button für Sent Emails */}
            {selectedAccount === 'sent' && hasMoreSentEmails && (
              <div className="smartInbox_load_more_container">
                <button 
                  className="smartInbox_load_more_button"
                  onClick={handleLoadMoreSent}
                  disabled={loadingSentMore}
                >
                  {loadingSentMore ? (
                    <>
                      <span className="load_more_spinner"></span>
                      <span>Loading more emails...</span>
                    </>
                  ) : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8 4v8m-4-4h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      <span>Load More (10 more emails)</span>
                    </>
                  )}
                </button>
                <p className="smartInbox_pagination_info">
                  Showing {sentEmails.length} sent emails
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Draft EmailAiPopup über Portal rendern */}
      {showDraftEmailPopup && draftEmailData && (
        <div className="popupOverlayWrapper" style={{ zIndex: 10000 }}>
          <div className="popupOverlayBackground"></div>
          <div className="popupContentWrapper">
            <EmailAiPopup 
              onClose={handleDraftEmailPopupClose} 
              rowData={draftEmailData} 
              userId={userId}
              isReply={true}
              source="draft"
              onEmailResult={(status, message) => {
                console.log(`📝 [DRAFT-POPUP] Email result: ${status} - ${message}`);
                if (status === 'success') {
                  handleDraftEmailPopupClose(true);
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Draft Bulk Mode über Portal rendern */}
      {showDraftBulkMode && bulkDrafts.length > 0 && (
        <div className="popupOverlayWrapper" style={{ zIndex: 10000 }}>
          <div className="popupOverlayBackground"></div>
          <div className="popupContentWrapper">
            <DraftBulkMode 
              onClose={handleDraftBulkModeClose}
              userId={userId}
              onEmailResult={(status, message) => {
                console.log(`📝 [DRAFT-BULK] Email result: ${status} - ${message}`);
              }}
              userSettings={userSettings}
              aiSettings={aiSettings}
              userTopics={userTopics}
              isTopicsLoaded={isTopicsLoaded}
              senderEmails={emailAccounts}
              topicTypeMapping={topicTypeMapping}
              bulkDrafts={bulkDrafts}
              initialDraftIndex={selectedDraftIndex}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartInbox;